/**
 * Role Helper Utilities
 *
 * Utility functions for role checking and user status management.
 * Refactored with Factory Pattern for better maintainability.
 */

import { AssignmentStatus, EnrollmentStatus, Login, UserRole } from '../apis/api-client';

/**
 * Configuration objects for roles and statuses
 * These replace switch statements with simple object lookups
 */
// Display labels for the four product-facing roles. The API enum keeps
// its existing `Admin` / `AccountAdmin` values for backend permission
// checks, but the UI shows the friendlier labels: Super Admin /
// Workspace Admin / Account Admin / Learner. "Group Admin" is NOT a
// UserRole — it's the per-group `user_group.is_admin` flag (and group
// owner), a concept one level below the workspace; don't conflate them.
const ROLE_CONFIG: Record<UserRole, { label: string; isAdminRole: boolean }> = {
  [UserRole.Admin]: { label: 'Super Admin', isAdminRole: true },
  [UserRole.AccountAdmin]: { label: 'Account Admin', isAdminRole: true },
  [UserRole.WorkspaceAdmin]: { label: 'Workspace Admin', isAdminRole: true },
  [UserRole.User]: { label: 'Learner', isAdminRole: false },
};

const ENROLLMENT_STATUS_CONFIG: Record<EnrollmentStatus, { label: string; color: 'default' | 'primary' | 'success' }> = {
  [EnrollmentStatus.NotStarted]: { label: 'Not Started', color: 'default' },
  [EnrollmentStatus.InProgress]: { label: 'In Progress', color: 'primary' },
  [EnrollmentStatus.Completed]: { label: 'Completed', color: 'success' },
};

const ASSIGNMENT_STATUS_CONFIG: Record<AssignmentStatus, { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' }> = {
  [AssignmentStatus.Upcoming]: { label: 'Upcoming', color: 'default' },
  [AssignmentStatus.Active]: { label: 'Active', color: 'primary' },
  [AssignmentStatus.Overdue]: { label: 'Overdue', color: 'error' },
  [AssignmentStatus.Completed]: { label: 'Completed', color: 'success' },
};

/**
 * Factory for creating role helper instances
 * Provides grouped methods for role-related operations
 */
class RoleHelperFactory {
  /**
   * Get a role helper instance for a specific role
   */
  static getRoleHelper(role?: UserRole) {
    const config = role !== undefined ? ROLE_CONFIG[role] : null;

    return {
      getLabel: (): string => config?.label ?? 'Unknown',
      isAdmin: (): boolean => config?.isAdminRole ?? false,
    };
  }

  /**
   * Get an enrollment status helper instance
   */
  static getEnrollmentHelper(status?: EnrollmentStatus) {
    const config = status !== undefined ? ENROLLMENT_STATUS_CONFIG[status] : null;

    return {
      getLabel: (): string => config?.label ?? 'Unknown',
      getColor: (): 'default' | 'primary' | 'success' => config?.color ?? 'default',
    };
  }

  /**
   * Get an assignment status helper instance
   */
  static getAssignmentHelper(status?: AssignmentStatus) {
    const config = status !== undefined ? ASSIGNMENT_STATUS_CONFIG[status] : null;

    return {
      getLabel: (): string => config?.label ?? 'Unknown',
      getColor: (): 'default' | 'primary' | 'success' | 'warning' | 'error' => config?.color ?? 'default',
    };
  }
}

/**
 * Checks if a user role is any type of admin
 * @param role UserRole enum value or string representation
 * @returns True if user is Admin, AccountAdmin, or WorkspaceAdmin
 */
export const isAdmin = (role?: UserRole): boolean => {
  if (role === undefined) return false;

  // Handle UserRole enum
  return ROLE_CONFIG[role]?.isAdminRole ?? false;
};

/**
 * Checks if user is Account Admin or System Admin (excludes Workspace Admin)
 * @param role UserRole enum value or string representation
 * @returns True if user is Admin or AccountAdmin
 */
export const isAccountOrSystemAdmin = (role?: UserRole): boolean => {
  if (role === undefined) return false;

  // Handle UserRole enum
  return role === UserRole.Admin || role === UserRole.AccountAdmin;
};

/**
 * Can this role author catalog content — create / edit / delete courses,
 * including bulk Publish / Unpublish / Delete?
 *
 * TARGET per CLAUDE.md "Role Model": **Super Admin only**. This still
 * admits the `AccountAdmin` (Account Admin) role because real accounts
 * are assigned that role today and a tighter gate would lock them out of
 * authoring. When the backend reseats those accounts as Super Admin,
 * narrow this to `role === UserRole.Admin` HERE — one place — and every
 * authoring gate (Course catalog, EditCoursePage, AdminCourseTable)
 * tightens with it. The client gate is cosmetic; the backend must
 * enforce authoring permissions regardless.
 */
export const isContentAuthorRole = (role?: UserRole): boolean =>
  role === UserRole.Admin || role === UserRole.AccountAdmin;

/**
 * Gets user's display status based on their account state
 * @param user Login object from API
 * @returns Status string
 */
export const getUserStatus = (user: Login): string => {
  if (user.isPendingInvitation) return 'Pending Invitation';
  if (user.isInvitationExpired) return 'Invitation Expired';
  if (!user.isActive) return 'Inactive';
  return 'Active';
};

/**
 * Gets MUI chip color for user status
 * @param user Login object from API
 * @returns MUI color name
 */
export const getStatusColor = (user: Login): 'success' | 'warning' | 'error' | 'default' => {
  if (user.isPendingInvitation) return 'warning';
  if (user.isInvitationExpired) return 'error';
  if (!user.isActive) return 'error';
  return 'success';
};

/**
 * Gets user's full display name or email fallback
 * @param user Login object from API
 * @returns Display name string
 */
export const getUserDisplayName = (user: Login): string => {
  const first = user.firstName || '';
  const last = user.lastName || '';
  const fullName = `${first} ${last}`.trim();
  return fullName || user.email || 'Unknown User';
};

/**
 * Gets readable label for UserRole enum
 * @param role UserRole enum value
 * @returns Human-readable role label
 */
export const getRoleLabel = (role?: UserRole): string => {
  return RoleHelperFactory.getRoleHelper(role).getLabel();
};

/**
 * Gets readable label for EnrollmentStatus enum
 * @param status EnrollmentStatus enum value
 * @returns Human-readable status label
 */
export const getEnrollmentStatusLabel = (status?: EnrollmentStatus): string => {
  return RoleHelperFactory.getEnrollmentHelper(status).getLabel();
};

/**
 * Gets MUI chip color for enrollment status
 * @param status EnrollmentStatus enum value
 * @returns MUI color name
 */
export const getEnrollmentStatusColor = (status?: EnrollmentStatus): 'default' | 'primary' | 'success' => {
  return RoleHelperFactory.getEnrollmentHelper(status).getColor();
};

/**
 * Gets readable label for AssignmentStatus enum
 * @param status AssignmentStatus enum value
 * @returns Human-readable status label
 */
export const getAssignmentStatusLabel = (status?: AssignmentStatus): string => {
  return RoleHelperFactory.getAssignmentHelper(status).getLabel();
};

/**
 * Gets MUI chip color for assignment status
 * @param status AssignmentStatus enum value
 * @returns MUI color name
 */
export const getAssignmentStatusColor = (status?: AssignmentStatus): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
  return RoleHelperFactory.getAssignmentHelper(status).getColor();
};

/**
 * Formats a date to a readable string
 * @param date Date object or undefined
 * @returns Formatted date string or dash
 */
export const formatDate = (date?: Date): string => {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
};

/**
 * Formats a date to include time
 * @param date Date object or undefined
 * @returns Formatted datetime string or dash
 */
export const formatDateTime = (date?: Date): string => {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

/**
 * Export the factory for advanced use cases
 * Most components should use the backward-compatible function exports above
 */
export { RoleHelperFactory };
